import { FieldError, useForm } from "react-hook-form";
import AuditTrailProductSearchComponent from "../AuditTrailProductSearchComponent";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import { useEffect, useState } from "react";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useAuditTrailActionAPI } from "@/app/hooks/api_hooks/Inventory/AuditTrail/useAuditTrailActionAPI";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { IAuditTrailExportCSVResponseFilter } from "@/api_framework/api_modals/Inventory";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useAuditTrailExportCSVAPI } from "@/app/hooks/api_hooks/Inventory/AuditTrail/useAuditTrailExportCSVAPI";
import { useAuditTrailExportCSVClinicAPI } from "@/app/hooks/api_hooks/Inventory/AuditTrail/useAuditTrailExportCSVClinicTamsAPI";
import { AHS_CLICK_EVENTS, AHS_SCREEN_IDS, useAhsInventoryClickLog } from "@/app/hooks/useAhsInventoryClickLog";

export interface IInventoryPurchaseOrderDraftProductInfo {
  variant_name?: string;
  variant_id: string;
  in_hand_quantity: number;
  price_per_package: number | null;
  lot_number: string;
  manufacturer_name: string;
  mrp: number;
  expiry_date: string;
}

export interface IAuditTrailProps {
  clinic_tams: string;
  product_info: IInventoryPurchaseOrderDraftProductInfo[];
  city: string[];
  clinics: Array<string>;
  action: Array<string>;
  productId: string;
  productName: string;
  type: string[];
}

const AuditTrailExportCSVFilterComponent = () => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 9);
  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const [defaultActionsSet, setDefaultActionsSet] = useState(false);
  const { getRouteKey } = useRouter();
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startDay}-${startMonth}-${startYear}`;
  const formattedEndDate = `${endDay}-${endMonth}-${endYear}`;
  const endDateEpoch = Math.floor(new Date(endDate.setHours(23, 59, 59, 999)).getTime() / 1000);
  const routeId = getRouteKey("CONFIGURATION", "id");
  const { routeState: cityRouteState } = useRouteInfo(routeId);
  const { routeState: actionRouteState } = useRouteInfo(getRouteKey("INVENTORY", "id"));
  const { cityList } = cityRouteState.state as IConfigurationListState;
  const { auditTrailAction, clinicTamsData } = actionRouteState.state as IInventoryState;
  const fetchCityList = useConfigurationCityListAPI();
  const cityListItem = useSelectMenuReducer(cityList, "name", "id");
  const actionItem = useSelectMenuReducer(auditTrailAction, "action_name", "action_id");
  const fetchActionList = useAuditTrailActionAPI();
  const { mappedClinicId, mappedCityId } = useAppSelector((state) => state.root.UIStates);
  const { execute: exportCSV } = useAuditTrailExportCSVAPI();
  const [loader, setLoader] = useState(false);
  const { execute: fetchClinicTamsData } = useAuditTrailExportCSVClinicAPI();
  const { logAhsEvent } = useAhsInventoryClickLog(AHS_SCREEN_IDS.AUDIT_TRAIL);
  const clinicsOrgItem = useSelectMenuReducer(clinicTamsData, "display_name", "id");

  const formHook = useForm<IAuditTrailProps>({
    mode: "onChange",
    defaultValues: {
      clinic_tams: "",
      product_info: [],
      type: [],
      city: [mappedCityId],
      clinics: [mappedClinicId],
      action: [],
    },
  });

  const types = [
    {
      id: "1",
      title: "TAMS",
    },
    {
      id: "2",
      title: "CLINIC",
    },
  ];

  useEffect(() => {
    fetchCityList();
    fetchActionList();
  }, []);

  useEffect(() => {
    if (auditTrailAction?.length && !defaultActionsSet) {
      const allActionIds = auditTrailAction.map((a) => a.action_id);
      formHook.setValue("action", allActionIds);
      setDefaultActionsSet(true);
    }
  }, [auditTrailAction, defaultActionsSet, formHook]);

  useEffect(() => {
    const subscription = formHook.watch((value, { name }) => {
      const citySelected = value.city?.length > 0;
      const typeSelected = value.type?.length > 0;
      if (name === "type" && citySelected && typeSelected) {
        let warehouse_type = "";
        const selectedTypes = value.type;
        const hasTAMS = selectedTypes.includes("1");
        const hasCLINIC = selectedTypes.includes("2");
        if (hasTAMS && hasCLINIC) {
          warehouse_type = "Both";
        } else if (hasTAMS) {
          warehouse_type = "TAMS";
        } else if (hasCLINIC) {
          warehouse_type = "CLINIC";
        }
        fetchClinicTamsData({
          city_ids: value.city,
          warehouse_type,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [formHook.watch]);

  const Submit = async (data: IAuditTrailProps) => {
    setLoader(true);
    const maxRangeDays = 10;
    const startDate = new Date(selectionDate.startDate);
    const calculatedEndDate = new Date(selectionDate.startDate);
    calculatedEndDate.setDate(startDate.getDate() + (maxRangeDays - 1));
    const endDateToUse =
      new Date(selectionDate.endDate) > calculatedEndDate ? calculatedEndDate : new Date(selectionDate.endDate);
    endDateToUse.setHours(23, 59, 59, 999);
    console.log(startDate);
    console.log(endDateToUse);
    const filters: IAuditTrailExportCSVResponseFilter = {
      vetic_variant_id: data?.productId || "all_variants",
      clinic_ids: data?.clinics,
      action_types: data?.action,
      start_date: Math.floor(startDate.getTime() / 1000),
      end_date: Math.floor(endDateToUse.getTime() / 1000),
    };
    logAhsEvent(AHS_CLICK_EVENTS.AUDIT_TRAIL_EXPORT_CSV_CLICK, {
      search_item: data?.productName,
      filter_values: filters,
    });
    await exportCSV(filters).then(() => {
      setLoader(false);
    });
  };

  return (
    <div className="bg-white p-2  rounded-xl w-full">
      <div className="justify-start text-black text-base font-semibold tracking-wide">Inventory Audit Trail Export CSV</div>

      <form onSubmit={formHook.handleSubmit(Submit)}>
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mt-3 w-full">
          <div className="w-full sm:w-2/5">
            <AuditTrailProductSearchComponent
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("productId", item.id);
                  formHook.setValue("productName", item.name);
                } else {
                  formHook.setValue("productId", null);
                  formHook.setValue("productName", null);
                }
              }}
              containerClassName="w-full shadow-none"
              inputContainerClassName="w-full"
              itemActiveClassName="bg-orange-500 text-white"
              flexibleAdd={false}
              searchType="Lab"
            />
          </div>
          <div className="w-full sm:w-auto">
            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formattedStartDate}
              endDate={formattedEndDate}
              handelClick={() => { }}
              containerClassName="w-full sm:w-[270px]"
              isFutureDateDisabled={true}
            />
          </div>
        </div>
        <div className="mt-2 sm:mt-5">
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 w-full">
            <SearchableMultiSelectMenu
              errorMessages={[{ message: null, type: "required" }]}
              onSelectItem={(item) => {
                const cityId = item.map((i) => i.id);
                formHook.setValue("city", cityId);
                formHook.setValue("clinics", []);
                formHook.setValue("type", []);
              }}
              fieldError={formHook.formState.errors.city}
              register={formHook.register("city", { required: true })}
              selectItems={cityListItem}
              placeholder="Select City"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={cityListItem.filter((c) => formHook.watch("city")?.includes(c.id))}
              containerClassName="w-full sm:w-[150px]"
              className="text-gray-800 !leading-5"
              withSelectAll
            />
            <SearchableMultiSelectMenu
              errorMessages={[{ message: null, type: "required" }]}
              onSelectItem={(item) => {
                const typeId = item.map((i) => i.id);
                formHook.setValue("type", typeId);
              }}
              fieldError={formHook.formState.errors.type}
              register={formHook.register("type", { required: true })}
              selectItems={types}
              placeholder="Select Type"
              showTooltips={true}
              showTypedErrors={true}
              defaultSelected={types.filter((c) => formHook.watch("type")?.includes(c.id))}
              containerClassName="w-full sm:w-[150px]"
              className="text-gray-800 !leading-5"
              withSelectAll
            />

            <SearchableMultiSelectMenu
              onSelectItem={(items) => {
                const clinicId = items.map((i) => i.id);
                formHook.setValue("clinics", clinicId);
                formHook.clearErrors("clinics");
              }}
              errorMessages={[{ message: "", type: "required" }]}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics", {
                required: true,
              })}              
              withSelectAll
              showTooltips={true}
              showTypedErrors={false}
              placeholder="Select Clinic"
              defaultSelected={clinicsOrgItem.filter((c) => formHook.watch("clinics")?.includes(c.id))}
              selectItems={clinicsOrgItem}
              containerClassName="w-full sm:w-[200px]"
              className="!leading-5"
               disabled={!formHook.watch("city")?.length || !formHook.watch("type")?.length}
            />

            <SearchableMultiSelectMenu
              errorMessages={[{ type: "required", message: "Action is required" }]}
              onSelectItem={(items) => {
                const actionId = items.map((i) => i.id);
                formHook.setValue("action", actionId);
                formHook.clearErrors("action");
              }}
              fieldError={(formHook?.formState?.errors?.action as FieldError) ?? null}
              register={formHook.register("action", { required: true })}
              selectItems={actionItem}
              placeholder="Select Action"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={actionItem.filter((c) => formHook.watch("action")?.includes(c.id))}
              containerClassName="w-full sm:w-[180px]"
              className="text-gray-800 !leading-5"
              withSelectAll
            />

            <div className="w-full sm:w-auto">
              <input
                type="submit"
                className="w-full sm:w-auto bg-black px-6 py-2 rounded-lg text-sm text-white cursor-pointer"
                value={loader ? "Loading..." : "Export as .CSV"}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AuditTrailExportCSVFilterComponent;